import { useEffect, useState } from "react";
import { X, TriangleAlert, Plus, FileDown, FileSpreadsheet, ArrowLeft, MapPin, Send, Loader2, Users, Trash2, Bell, MessageSquare, Inbox } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API, openDownload } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import FaultBroadcastDialog from "@/components/FaultBroadcastDialog";
import SmsInboxModal from "@/components/SmsInboxModal";

const STATUSES = ["nowa", "przyjęta", "w toku", "wstrzymana", "rozwiązana", "zamknięta"];
const PRIORITIES = ["niski", "średni", "wysoki", "krytyczny"];
const LINK_MODULES = [
  { key: "cables", label: "Kable" }, { key: "closures", label: "Mufy" },
  { key: "splitters", label: "Splittery" }, { key: "olt", label: "OLT" }, { key: "clients", label: "Klienci" },
];
const ST_STYLE = {
  "nowa": "bg-blue-500/15 text-blue-300 border-blue-500/50",
  "przyjęta": "bg-cyan-500/15 text-cyan-300 border-cyan-500/50",
  "w toku": "bg-amber-500/15 text-amber-300 border-amber-500/50",
  "wstrzymana": "bg-slate-500/15 text-slate-300 border-slate-500/50",
  "rozwiązana": "bg-green-500/15 text-green-300 border-green-500/50",
  "zamknięta": "bg-slate-700/50 text-slate-400 border-slate-600",
};
const PR_STYLE = {
  "niski": "text-slate-300", "średni": "text-cyan-300", "wysoki": "text-amber-300", "krytyczny": "text-red-400",
};

export default function FaultsPanel({ role, onClose, onLocate, onRefresh }) {
  const { t } = useI18n();
  const canEdit = role !== "odczyt";
  const canDelete = role === "admin" || role === "projektant";
  const delFault = async (row) => {
    if (!window.confirm(t("Usunąć to zgłoszenie awarii? Tej operacji nie można cofnąć."))) return;
    try { await api.delete(`/tickets/${row.id}`); toast.success(t("Usunięto awarię")); setSel(null); load(); onRefresh && onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || t("Nie udało się usunąć awarii")); }
  };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [techs, setTechs] = useState([]);
  const [linkObjs, setLinkObjs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [sel, setSel] = useState(null);
  const [affected, setAffected] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [fq, setFq] = useState({ q: "", status: "all", priority: "all" });
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastInitial, setBroadcastInitial] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", priority: "średni", category: "", deadline: "", eta: "", assignee_id: "", link_module: "", link_id: "" });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/faults"); setRows((data || []).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); api.get("/technicians").then((r) => setTechs(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!form.link_module) { setLinkObjs([]); return; }
    api.get(`/${form.link_module}`).then((r) => setLinkObjs(r.data || [])).catch(() => setLinkObjs([]));
    setForm((f) => ({ ...f, link_id: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.link_module]);

  const openDetail = async (row) => {
    setSel(row); setAffected(null);
    try { const { data } = await api.get(`/tickets/${row.id}/affected`); setAffected(data); } catch { setAffected({ count: 0, clients: [] }); }
  };

  const create = async () => {
    if (!form.title.trim()) return toast.error(t("Podaj tytuł zgłoszenia"));
    setBusy(true);
    try {
      const tech = techs.find((x) => x.id === form.assignee_id);
      const lobj = linkObjs.find((x) => x.id === form.link_id);
      await api.post("/tickets", {
        ...form,
        assignee_name: tech ? (tech.name || tech.technician) : "",
        link_label: lobj ? (lobj.number || lobj.name || lobj.id) : "",
      });
      toast.success(t("Zgłoszenie utworzone"));
      setShowForm(false);
      const linkMod = form.link_module, linkId = form.link_id, ttl = form.title;
      setForm({ title: "", description: "", priority: "średni", category: "", deadline: "", eta: "", assignee_id: "", link_module: "", link_id: "" });
      load(); onRefresh && onRefresh();
      if (linkId && (linkMod === "closures" || linkMod === "splitters")) {
        const init = { title: ttl, kind: "awaria" };
        if (linkMod === "closures") init.closureIds = [linkId]; else init.splitterIds = [linkId];
        if (window.confirm(t("Powiadomić klientów dotkniętych tym obiektem o awarii?"))) {
          setBroadcastInitial(init); setShowBroadcast(true);
        }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (row, status) => {
    try { const { data } = await api.post(`/tickets/${row.id}/status`, { status }); setSel(data); load(); onRefresh && onRefresh(); toast.success(t("Zaktualizowano status")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveAssign = async (row, patch) => {
    try { const { data } = await api.post(`/tickets/${row.id}/assign`, patch); setSel(data); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendComment = async () => {
    if (!comment.trim()) return;
    try { const { data } = await api.post(`/tickets/${sel.id}/comment`, { text: comment.trim() }); setSel(data); setComment(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const dl = (path) => openDownload(path);
  const slaCheck = async () => {
    try {
      const { data } = await api.post("/tickets/sla-check");
      toast.success(`${t("Sprawdzono SLA")} · ${t("Po terminie")}: ${data.overdue} · ${t("Ostrzeżeń")}: ${data.warned}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const overdue = (r) => r.deadline && r.deadline < new Date().toISOString().slice(0, 10) && !["rozwiązana", "zamknięta"].includes(r.status);

  const [showInbox, setShowInbox] = useState(false);
  const openInbox = () => setShowInbox(true);

  const filtered = rows.filter((r) =>
    (fq.status === "all" || r.status === fq.status) &&
    (fq.priority === "all" || r.priority === fq.priority) &&
    (!fq.q || `${r.ticket_no} ${r.title} ${r.link_label} ${r.assignee_name}`.toLowerCase().includes(fq.q.toLowerCase())));

  const inp = "bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm w-full";

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="faults-panel">
      {showBroadcast && <FaultBroadcastDialog initial={broadcastInitial} onClose={() => { setShowBroadcast(false); setBroadcastInitial(null); }} />}
      {showInbox && <SmsInboxModal onClose={() => setShowInbox(false)} onChanged={() => { load(); onRefresh && onRefresh(); }} canEdit={canEdit} canDelete={canDelete} />}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2"><TriangleAlert size={18} className="text-red-400" />
          <h2 className="font-heading font-bold text-lg">{sel ? `${sel.ticket_no}` : t("Rejestr zgłoszeń")}</h2></div>
        <div className="flex items-center gap-2">
          {!sel && <>
            {canEdit && <button onClick={() => setShowBroadcast(true)} data-testid="faults-broadcast-btn" title={t("Powiadom klientów o awarii/pracach")} className="p-1.5 rounded hover:bg-slate-700 text-amber-300"><Bell size={17} /></button>}
            {canEdit && <button onClick={slaCheck} data-testid="faults-sla-check" title={t("Sprawdź SLA teraz")} className="p-1.5 rounded hover:bg-slate-700 text-amber-300"><TriangleAlert size={17} /></button>}
            {canEdit && <button onClick={openInbox} data-testid="faults-inbox-btn" title={t("Skrzynka SMS")} className="p-1.5 rounded hover:bg-slate-700 text-cyan-300"><Inbox size={17} /></button>}
            <button onClick={() => dl("tickets-register.pdf")} data-testid="faults-register-pdf" title={t("Rejestr PDF")} className="p-1.5 rounded hover:bg-slate-700 text-red-300"><FileDown size={17} /></button>
            <button onClick={() => dl("tickets-register.csv")} data-testid="faults-register-csv" title={t("Rejestr CSV")} className="p-1.5 rounded hover:bg-slate-700 text-green-300"><FileSpreadsheet size={17} /></button>
          </>}
          <button onClick={onClose} data-testid="faults-close" className="p-1.5 rounded hover:bg-slate-700"><X size={18} /></button>
        </div>
      </div>

      {sel ? (
        <div className="overflow-y-auto p-5 space-y-4" data-testid="fault-detail">
          <button onClick={() => setSel(null)} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"><ArrowLeft size={14} /> {t("Wróć do listy")}</button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] rounded px-1.5 py-0.5 border ${ST_STYLE[sel.status] || ""}`}>{t(sel.status)}</span>
              <span className={`text-xs font-semibold ${PR_STYLE[sel.priority] || ""}`}>{t(sel.priority)}</span>
              {overdue(sel) && <span className="text-[10px] rounded px-1.5 py-0.5 border border-red-500/60 text-red-400">{t("Po terminie")}</span>}
            </div>
            <h3 className="font-heading font-bold text-base mt-1">{sel.title}</h3>
            <p className="text-sm text-slate-400 whitespace-pre-wrap">{sel.description || "—"}</p>
            {canDelete && (
              <button onClick={() => delFault(sel)} data-testid="fault-delete"
                className="mt-2 text-xs bg-red-900/40 hover:bg-red-900/60 text-red-300 rounded px-3 py-1.5 flex items-center gap-1.5"><Trash2 size={14} /> {t("Usuń awarię")}</button>
            )}
          </div>

          {canEdit && (
            <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700 space-y-2">
              <div>
                <label className="text-[11px] text-slate-400">{t("Zmień status")}</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(sel, s)} data-testid={`fault-status-${s}`}
                      className={`text-[11px] rounded px-2 py-1 border ${sel.status === s ? "bg-cyan-600 border-cyan-500 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}>{t(s)}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-slate-400">{t("Przypisz montera")}</label>
                  <select value={sel.assignee_id || ""} data-testid="fault-assignee"
                    onChange={(e) => { const tec = techs.find((x) => x.id === e.target.value); saveAssign(sel, { assignee_id: e.target.value, assignee_name: tec ? (tec.name || tec.technician) : "", _log: "Zmiana montera" }); }}
                    className={inp}>
                    <option value="">{t("— brak —")}</option>
                    {techs.map((x) => <option key={x.id} value={x.id}>{x.name || x.technician || x.id}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-slate-400">{t("Priorytet")}</label>
                  <select value={sel.priority} data-testid="fault-priority" onChange={(e) => saveAssign(sel, { priority: e.target.value, _log: "Zmiana priorytetu" })} className={inp}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{t(p)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-slate-400">{t("Termin (SLA)")}</label>
                  <input type="date" value={sel.deadline || ""} data-testid="fault-deadline" onChange={(e) => saveAssign(sel, { deadline: e.target.value, _log: "Zmiana terminu" })} className={inp} />
                </div>
                <div>
                  <label className="text-[11px] text-slate-400">{t("Planowane usunięcie (ETA)")}</label>
                  <input type="datetime-local" value={sel.eta || ""} data-testid="fault-eta" onChange={(e) => saveAssign(sel, { eta: e.target.value, _log: "Zmiana ETA" })} className={inp} />
                </div>
                {sel.lat != null && <button onClick={() => onLocate && onLocate(sel)} className="self-end text-xs text-cyan-400 flex items-center gap-1"><MapPin size={13} /> {t("Pokaż na mapie")}</button>}
              </div>
            </div>
          )}

          <div className="text-xs text-slate-400 grid grid-cols-2 gap-y-1">
            <span>{t("Zgłoszono")}: <b className="text-slate-300">{sel.reported_at || "—"}</b></span>
            <span>{t("Termin (SLA)")}: <b className={overdue(sel) ? "text-red-400" : "text-slate-300"}>{sel.deadline || "—"}</b></span>
            <span>{t("Planowane usunięcie (ETA)")}: <b className="text-emerald-300" data-testid="fault-eta-display">{sel.eta ? sel.eta.replace("T", " ") : "—"}</b></span>
            <span>{t("Rozwiązano")}: <b className="text-slate-300">{sel.resolved_at || "—"}</b></span>
            <span>{t("Monter")}: <b className="text-slate-300">{sel.assignee_name || "—"}</b></span>
            <span className="col-span-2">{t("Powiązany obiekt")}: <b className="text-slate-300">{sel.link_label || "—"}</b></span>
          </div>

          <div className="bg-slate-800/40 rounded-lg p-3 border border-slate-700">
            <div className="text-xs font-semibold text-slate-300 flex items-center gap-1.5 mb-2"><Users size={14} className="text-amber-400" /> {t("Dotknięci klienci")} {affected ? `(${affected.count})` : ""}</div>
            {!affected ? <div className="text-xs text-slate-500">{t("Ładowanie…")}</div> :
              affected.count === 0 ? <div className="text-xs text-slate-500">—</div> :
                <ul className="text-xs text-slate-300 space-y-0.5 max-h-32 overflow-y-auto">{affected.clients.map((c) => <li key={c.id}>• {c.name} <span className="text-slate-500">{c.address}</span></li>)}</ul>}
          </div>

          {(sel.snmp_clients || []).length > 0 && (
            <div className="bg-slate-800/40 rounded-lg p-3 border border-cyan-800/40" data-testid="fault-snmp-clients">
              <div className="text-xs font-semibold text-slate-300 flex items-center gap-1.5 mb-2"><MapPin size={14} className="text-cyan-400" /> {t("Klienci dotknięci (SNMP)")}</div>
              <ul className="text-xs text-slate-300 space-y-1.5 max-h-44 overflow-y-auto">
                {sel.snmp_clients.map((c, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <div className="flex-1">
                      <b>{c.name || "—"}</b> <span className="text-slate-500">ONU {c.onu_id} · {c.rx_dbm} dBm</span>
                      <div className="text-slate-500">{c.address || ""}{c.phone ? ` · ${c.phone}` : ""}{c.contract_no ? ` · ${c.contract_no}` : ""}</div>
                      {(c.sn || c.mac) && <div className="text-slate-500">{c.sn ? `S/N: ${c.sn}` : ""}{c.mac ? `${c.sn ? " · " : ""}MAC: ${c.mac}` : ""}</div>}
                    </div>
                    {c.lat != null && onLocate && <button onClick={() => onLocate({ lat: c.lat, lng: c.lng })} data-testid={`fault-snmp-locate-${i}`} className="text-cyan-400 hover:text-cyan-300 shrink-0"><MapPin size={14} /></button>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-slate-300 mb-2">{t("Historia")}</div>
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {(sel.events || []).slice().reverse().map((ev, i) => {
                const isSms = ev.action === "sms_in";
                return (
                  <div key={i} data-testid={isSms ? "fault-sms-event" : undefined}
                    className={`text-xs border-l-2 pl-2 ${isSms ? "border-cyan-500" : "border-slate-700"}`}>
                    <span className="text-slate-500">{(ev.ts || "").slice(0, 16).replace("T", " ")}</span> · <b className={isSms ? "text-cyan-300" : "text-slate-300"}>{ev.user}</b>
                    {isSms ? (
                      <div className="flex items-start gap-1.5 text-cyan-100 bg-cyan-950/30 rounded px-1.5 py-1 mt-0.5">
                        <MessageSquare size={12} className="mt-0.5 shrink-0 text-cyan-400" />
                        <span>{ev.text}</span>
                      </div>
                    ) : (
                      <div className="text-slate-400">{t(ev.action)}: {ev.text}</div>
                    )}
                  </div>
                );
              })}
            </div>
            {canEdit && (
              <div className="flex gap-2 mt-2">
                <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("Dodaj komentarz")} data-testid="fault-comment" className={inp}
                  onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }} />
                <button onClick={sendComment} data-testid="fault-comment-send" className="bg-cyan-600 hover:bg-cyan-500 rounded px-3"><Send size={15} /></button>
              </div>
            )}
          </div>

          <button onClick={() => dl(`tickets/${sel.id}/report.pdf`)} data-testid="fault-report-pdf"
            className="w-full bg-slate-700 hover:bg-slate-600 rounded py-2 text-sm flex items-center justify-center gap-2"><FileDown size={15} /> {t("Protokół PDF")}</button>
        </div>
      ) : (
        <div className="overflow-y-auto p-5 space-y-4">
          {canEdit && (showForm ? (
            <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700 space-y-2" data-testid="fault-form">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("Tytuł")} data-testid="fault-title" className={inp} />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("Opis")} data-testid="fault-desc" rows={2} className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="fault-form-priority" className={inp}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{t(p)}</option>)}
                </select>
                <input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} title={t("Termin (SLA)")} data-testid="fault-form-deadline" className={inp} />
                <input type="datetime-local" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} title={t("Planowane usunięcie (ETA)")} data-testid="fault-form-eta" className={inp} />
                <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} data-testid="fault-form-assignee" className={inp}>
                  <option value="">{t("Przypisz montera")}</option>
                  {techs.map((x) => <option key={x.id} value={x.id}>{x.name || x.technician || x.id}</option>)}
                </select>
                <select value={form.link_module} onChange={(e) => setForm({ ...form, link_module: e.target.value })} data-testid="fault-form-linkmod" className={inp}>
                  <option value="">{t("Powiązany obiekt")}</option>
                  {LINK_MODULES.map((m) => <option key={m.key} value={m.key}>{t(m.label)}</option>)}
                </select>
                {form.link_module && (
                  <select value={form.link_id} onChange={(e) => setForm({ ...form, link_id: e.target.value })} data-testid="fault-form-linkobj" className={`${inp} col-span-2`}>
                    <option value="">{t("— wybierz obiekt —")}</option>
                    {linkObjs.map((o) => <option key={o.id} value={o.id}>{o.number || o.name || o.id}</option>)}
                  </select>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={create} disabled={busy} data-testid="fault-create" className="flex-1 bg-red-600 hover:bg-red-500 disabled:opacity-60 rounded py-2 text-sm flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {t("Utwórz zgłoszenie")}</button>
                <button onClick={() => setShowForm(false)} className="px-4 rounded bg-slate-700 hover:bg-slate-600 text-sm">{t("Anuluj")}</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowForm(true)} data-testid="fault-new" className="w-full bg-red-600 hover:bg-red-500 rounded py-2.5 text-sm flex items-center justify-center gap-2"><Plus size={16} /> {t("Nowe zgłoszenie")}</button>
          ))}

          <div className="flex items-center gap-2 text-xs">
            <input value={fq.q} onChange={(e) => setFq({ ...fq, q: e.target.value })} placeholder={t("Szukaj…")} data-testid="fault-search" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 flex-1" />
            <select value={fq.status} onChange={(e) => setFq({ ...fq, status: e.target.value })} data-testid="fault-filter-status" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5">
              <option value="all">{t("Wszystkie statusy")}</option>
              {STATUSES.map((s) => <option key={s} value={s}>{t(s)}</option>)}
            </select>
            <select value={fq.priority} onChange={(e) => setFq({ ...fq, priority: e.target.value })} data-testid="fault-filter-priority" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5">
              <option value="all">{t("Wszystkie priorytety")}</option>
              {PRIORITIES.map((p) => <option key={p} value={p}>{t(p)}</option>)}
            </select>
          </div>

          {loading ? <div className="text-slate-500 text-sm">{t("Ładowanie…")}</div> :
            filtered.length === 0 ? <div className="text-slate-500 text-sm text-center py-8">{t("Brak zgłoszeń")}</div> : (
              <div className="space-y-2" data-testid="fault-list">
                {filtered.map((r) => (
                  <button key={r.id} onClick={() => openDetail(r)} data-testid={`fault-row-${r.id}`}
                    className="w-full text-left bg-slate-800/50 border border-slate-700 rounded-lg p-3 hover:border-slate-600">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-slate-400">{r.ticket_no}</span>
                      <span className={`text-[10px] rounded px-1.5 py-0.5 border ${ST_STYLE[r.status] || ""}`}>{t(r.status)}</span>
                      <span className={`text-[11px] font-semibold ${PR_STYLE[r.priority] || ""}`}>{t(r.priority)}</span>
                      {overdue(r) && <span className="text-[10px] rounded px-1.5 py-0.5 border border-red-500/60 text-red-400">{t("Po terminie")}</span>}
                    </div>
                    <div className="text-sm text-slate-200 mt-0.5">{r.title}</div>
                    <div className="text-[11px] text-slate-500">{r.link_label ? `${r.link_label} · ` : ""}{r.assignee_name || t("— brak —")}{r.deadline ? ` · ${t("Termin (SLA)")}: ${r.deadline}` : ""}</div>
                  </button>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}
